import { useEffect, useRef } from 'react';

/**
 * Custom hook that routes wheel scroll events between an outer page container
 * and an inner table container with asymmetric behavior:
 * - Scroll DOWN: page-first (until page can't scroll), then table
 * - Scroll UP: table-first (until table can't scroll), then page
 *
 * This enables the UX where users scroll the page to reach the table,
 * then scroll within the table, with natural scroll-up behavior.
 */
export function useWheelScrollRouting(
  tableContainerRef: React.RefObject<HTMLDivElement>
) {
  const outerScrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const tableEl = tableContainerRef.current;
    if (!tableEl) return;

    const findAndCacheOuterScrollContainer = () => {
      if (outerScrollContainerRef.current) {
        if (document.contains(outerScrollContainerRef.current)) {
          return outerScrollContainerRef.current;
        }
      }

      let node: HTMLElement | null = tableEl.parentElement;
      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowY === 'overlay') &&
          node.scrollHeight > node.clientHeight;

        if (canScrollY) {
          outerScrollContainerRef.current = node;
          return node;
        }
        node = node.parentElement;
      }

      const scrollingEl = document.scrollingElement;
      const fallback = scrollingEl instanceof HTMLElement ? scrollingEl : null;
      outerScrollContainerRef.current = fallback;
      return fallback;
    };

    let rafId: number | null = null;

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;

      const absDeltaY = Math.abs(e.deltaY);
      const absDeltaX = Math.abs(e.deltaX);
      if (absDeltaY <= absDeltaX) return;

      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const outer = findAndCacheOuterScrollContainer();
        if (!outer) return;

        const epsilon = 1;

        const outerMaxScrollTop = Math.max(
          0,
          outer.scrollHeight - outer.clientHeight
        );
        const canScrollDownPage = outer.scrollTop < outerMaxScrollTop - epsilon;
        const canScrollUpPage = outer.scrollTop > epsilon;
        const canScrollUpTable = tableEl.scrollTop > epsilon;

        if (e.deltaY > 0 && canScrollDownPage) {
          e.preventDefault();
          outer.scrollBy({ top: e.deltaY, behavior: 'auto' });
          return;
        }

        if (e.deltaY < 0 && canScrollUpTable) {
          return;
        }

        if (e.deltaY < 0 && canScrollUpPage) {
          e.preventDefault();
          outer.scrollBy({ top: e.deltaY, behavior: 'auto' });
        }
      });
    };

    const opts = { passive: false, capture: true } as const;
    tableEl.addEventListener('wheel', handleWheel, opts);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      tableEl.removeEventListener('wheel', handleWheel, opts);
      outerScrollContainerRef.current = null;
    };
  }, [tableContainerRef]);
}
